/* eslint-disable @typescript-eslint/no-require-imports */
/**
 * Platform-specific Firebase abstraction layer
 * Handles the differences between web and native Firebase implementations
 * and provides a unified interface for auth and firestore
 */
import { Platform } from "react-native";
import { initializeApp, getApps } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import config from "@/config";

// ============================================================================
// Internal Firebase Platform Abstraction Types
// ============================================================================

/**
 * Firebase user interface abstraction
 * Unified interface for Firebase user across web and native platforms
 */
interface FirebaseUser {
  /** Unique user identifier */
  uid: string;
  /** User's email address, may be null for some auth providers */
  email: string | null;
  /** User's display name, may be null if not provided */
  displayName: string | null;
  /** User's phone number, may be null if not provided */
  phoneNumber: string | null;
  /** URL to user's profile photo, may be null */
  photoURL: string | null;
  /** User's authentication provider ID */
  providerId: string;
  /** Whether the user's email has been verified */
  emailVerified: boolean;
  /** Get the user's Firebase ID token */
  getIdToken(): Promise<string>;
}

/**
 * Google Sign-in result structure
 * Unified response structure for Google authentication across platforms
 */
interface GoogleSignInResult {
  /** Sign-in data containing authentication tokens */
  data?: {
    /** Google ID token for Firebase authentication */
    idToken: string;
  };
}

/**
 * Firebase Auth service interface abstraction
 * Unified interface for Firebase authentication across web and native platforms
 */
interface FirebaseAuth {
  /** Currently authenticated user, null if not signed in */
  currentUser: FirebaseUser | null;
  /** Listen to authentication state changes */
  onAuthStateChanged(callback: (user: FirebaseUser | null) => void): () => void;
  /** Sign in with a credential (Google, etc.) */
  signInWithCredential(credential: any): Promise<{ user: FirebaseUser }>;
  /** Sign out the current user */
  signOut(): Promise<void>;
  /** Create a new account with email and password */
  createUserWithEmailAndPassword(
    email: string,
    password: string
  ): Promise<{ user: FirebaseUser }>;
  /** Sign in with email and password */
  signInWithEmailAndPassword(
    email: string,
    password: string
  ): Promise<{ user: FirebaseUser }>;
  /** Send a password reset email */
  sendPasswordResetEmail(email: string): Promise<void>;
  /** Send email verification to a user */
  sendEmailVerification(user: FirebaseUser): Promise<void>;
}

/**
 * Google Auth Provider interface abstraction
 * Unified interface for creating Google credentials across platforms
 */
interface GoogleAuthProvider {
  /** Create a Google credential from an ID token */
  credential(idToken: string): any;
}

/**
 * Google Sign-in service interface abstraction
 * Unified interface for Google Sign-in across web and native platforms
 */
interface GoogleAuthSignIn {
  /** Check if Google Play services are available (Android only, optional) */
  hasPlayServices?(options?: any): Promise<boolean>;
  /** Initiate Google Sign-in flow */
  signIn(): Promise<GoogleSignInResult>;
  /** Sign out from Google */
  signOut(): Promise<void>;
}

// ============================================================================
// Platform-Specific Implementation
// ============================================================================

// Platform-specific implementations
let firebaseAuth: FirebaseAuth;
let googleAuthProvider: GoogleAuthProvider;
let googleAuthSignIn: GoogleAuthSignIn;

if (Platform.OS === "web") {
  // Web implementation using Firebase web SDK
  // https://firebase.google.com/docs/auth/web/google-signin
  const {
    getAuth,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithCredential,
    createUserWithEmailAndPassword: webCreateUser,
    signInWithEmailAndPassword: webSignInEmail,
    sendPasswordResetEmail: webSendReset,
    sendEmailVerification: webSendVerification
  } = require("firebase/auth");

  // Initialize Firebase web SDK
  if (!getApps().length) {
    initializeApp({
      apiKey: config.firebase.apiKey,
      authDomain: config.firebase.authDomain,
      projectId: config.firebase.projectId,
      appId: config.firebase.appId
    });
  }

  const webAuth = getAuth();
  const WebGoogleAuthProvider = GoogleAuthProvider;

  // Web Firebase Auth implementation
  firebaseAuth = {
    get currentUser() {
      return webAuth.currentUser;
    },
    onAuthStateChanged(callback: (user: FirebaseUser | null) => void) {
      return webAuth.onAuthStateChanged(callback);
    },
    async signInWithCredential(credential: any) {
      const result = await signInWithCredential(webAuth, credential);
      return { user: result.user };
    },
    async signOut() {
      await webAuth.signOut();
    },
    async createUserWithEmailAndPassword(email: string, password: string) {
      const result = await webCreateUser(webAuth, email, password);
      return { user: result.user };
    },
    async signInWithEmailAndPassword(email: string, password: string) {
      const result = await webSignInEmail(webAuth, email, password);
      return { user: result.user };
    },
    async sendPasswordResetEmail(email: string) {
      await webSendReset(webAuth, email);
    },
    async sendEmailVerification(user: FirebaseUser) {
      await webSendVerification(user);
    }
  };

  // Web Google Auth Provider
  googleAuthProvider = {
    credential(idToken: string) {
      return WebGoogleAuthProvider.credential(idToken);
    }
  };

  // Web Google Sign-in implementation
  googleAuthSignIn = {
    async signIn() {
      const provider = new WebGoogleAuthProvider();
      const result = await signInWithPopup(webAuth, provider);
      const idToken = await result.user.getIdToken();
      return {
        data: {
          idToken
        }
      };
    },
    async signOut() {
      await webAuth.signOut();
    }
  };
} else {
  // Native implementation using React Native Firebase
  const {
    getAuth,
    GoogleAuthProvider
  } = require("@react-native-firebase/auth");

  const { GoogleSignin } = require("@react-native-google-signin/google-signin");

  // Configure Google Sign-in for native platforms
  GoogleSignin.configure({
    webClientId: config.firebase.webClientId
  });

  // Native Firebase Auth — wrap to match our FirebaseAuth interface
  const nativeAuth = getAuth();
  firebaseAuth = {
    get currentUser() {
      return nativeAuth.currentUser;
    },
    onAuthStateChanged(cb: (user: FirebaseUser | null) => void) {
      return nativeAuth.onAuthStateChanged(cb);
    },
    signInWithCredential(cred: any) {
      return nativeAuth.signInWithCredential(cred);
    },
    signOut() {
      return nativeAuth.signOut();
    },
    createUserWithEmailAndPassword(e: string, p: string) {
      return nativeAuth.createUserWithEmailAndPassword(e, p);
    },
    signInWithEmailAndPassword(e: string, p: string) {
      return nativeAuth.signInWithEmailAndPassword(e, p);
    },
    sendPasswordResetEmail(e: string) {
      return nativeAuth.sendPasswordResetEmail(e);
    },
    async sendEmailVerification(user: FirebaseUser) {
      await (user as any).sendEmailVerification();
    }
  };
  googleAuthProvider = GoogleAuthProvider;
  googleAuthSignIn = GoogleSignin;
}

// ============================================================================
// Firestore Implementation
// ============================================================================

// TODO: Consider using @react-native-firebase/firestore for native platforms
// For now, using firebase/firestore for all platforms for simplicity
const firestore = getFirestore();

// ============================================================================
// Exports
// ============================================================================

export { firebaseAuth, googleAuthProvider, googleAuthSignIn, firestore };
export type { FirebaseUser };
